import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

/**
 * A reusable dropdown component that matches the LineLeaders design system
 * @param {Object} props
 * @param {string} props.title - The title shown when no item is selected
 * @param {Array<{value: string, label: string, icon?: React.ReactNode}>} props.items - Array of dropdown items
 * @param {function} props.onSelect - Callback when an item is selected
 * @param {string} props.activeItem - Currently selected item value
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} [props.icon] - Optional icon shown before the title
 * @param {string} [props.id] - Optional ID for the dropdown
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Size variant
 * @param {'default' | 'outline'} [props.variant='default'] - Style variant
 * @param {boolean} [props.disabled=false] - Whether the dropdown is disabled
 */
const Dropdown = ({ 
  title,
  items,
  onSelect,
  activeItem,
  className = '',
  icon = null,
  id = '',
  size = 'md',
  variant = 'default',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [minWidth, setMinWidth] = useState(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate minimum width based on content
  useEffect(() => {
    if (buttonRef.current) {
      // Get the button's padding
      const buttonStyle = window.getComputedStyle(buttonRef.current);
      const buttonPadding = parseFloat(buttonStyle.paddingLeft) + parseFloat(buttonStyle.paddingRight);
      
      // Create a temporary div to measure text width
      const temp = document.createElement('div');
      temp.style.position = 'absolute';
      temp.style.visibility = 'hidden';
      temp.style.whiteSpace = 'nowrap';
      temp.style.fontSize = buttonStyle.fontSize;
      temp.style.fontFamily = buttonStyle.fontFamily;
      document.body.appendChild(temp);

      // Find the widest text among title and all items
      let maxWidth = 0;
      temp.textContent = title;
      maxWidth = Math.max(maxWidth, temp.offsetWidth);

      items.forEach(item => {
        temp.textContent = item.label;
        maxWidth = Math.max(maxWidth, temp.offsetWidth);
      });

      // Add padding, icon space, and chevron width
      const iconSpace = icon ? 24 : 0; // Assuming icon is 20px + margin
      const chevronWidth = 20; // Chevron icon width
      const totalWidth = maxWidth + buttonPadding + iconSpace + chevronWidth + 8; // +8 for safety margin

      setMinWidth(totalWidth);
      document.body.removeChild(temp);
    }
  }, [items, title, icon]);

  // Get the currently selected item's label
  const getSelectedLabel = () => {
    const selected = items.find(item => item.value === activeItem);
    return selected ? selected.label : title;
  };

  // Size-specific classes
  const sizeClasses = {
    sm: 'text-xs py-1.5 px-2',
    md: 'text-sm py-2 px-3',
    lg: 'text-base py-2.5 px-4',
  };

  // Variant-specific classes
  const variantClasses = {
    default: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200',
    outline: 'bg-transparent border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200',
  };

  // Disabled state classes
  const disabledClasses = disabled 
    ? 'opacity-50 cursor-not-allowed' 
    : 'hover:bg-gray-50 dark:hover:bg-gray-700';

  // Extract error state from className
  const hasError = className.includes('ring-brand-red-500');

  return (
    <div 
      ref={dropdownRef}
      className="relative inline-block"
      id={id}
      style={{ minWidth: `${minWidth}px` }}
    >
      <button 
        ref={buttonRef}
        className={`inline-flex items-center justify-between rounded-md w-full
          ${sizeClasses[size]} 
          ${variantClasses[variant]} 
          ${disabledClasses}
          ${isOpen ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-gray-400 dark:ring-gray-500 ring-opacity-50' : ''}
          ${hasError ? className : ''}
          transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 ${hasError ? 'focus:ring-brand-red-500' : 'focus:ring-gray-400 dark:focus:ring-gray-500'} focus:ring-opacity-50 shadow-sm`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="flex items-center min-w-0 flex-shrink">
          {icon && <span className="mr-2 flex-shrink-0">{icon}</span>}
          <span className="truncate">{getSelectedLabel()}</span>
        </span>
        <ChevronDownIcon 
          className={`ml-2 h-4 w-4 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-180 transform' : ''}`}
          aria-hidden="true"
        />
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto"
          style={{ minWidth: `${minWidth}px` }}
        >
          <div className="py-1" role="listbox">
            {items.map((item) => (
              <button
                key={item.value}
                className={`flex w-full items-center px-3 py-2 text-left text-sm 
                  ${activeItem === item.value 
                    ? 'bg-brand-red-50 dark:bg-brand-red-900/20 text-brand-red-700 dark:text-brand-red-400' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                onClick={() => {
                  onSelect(item.value);
                  setIsOpen(false);
                }}
                role="option"
                aria-selected={activeItem === item.value}
              >
                {item.icon && (
                  <span className="mr-2 flex-shrink-0">
                    {item.icon}
                  </span>
                )}
                <span className={`truncate ${activeItem === item.value ? 'font-medium' : ''}`}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
